"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";

export const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(function RadioGroup({ className, ...props }, ref) {
  return (
    <RadioGroupPrimitive.Root
      ref={ref}
      className={cn(
        "grid gap-2",
        // Invalid-state hook on the group root — child radios paint a
        // red border via the `group-aria-[invalid=true]` selector below.
        // Done as a CSS-driven cascade rather than threading aria-invalid
        // into every item so the field component only needs to set the
        // attribute once.
        "group/radio",
        className,
      )}
      {...props}
    />
  );
});

export const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(function RadioGroupItem({ className, ...props }, ref) {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      // Radix renders a hidden `<input type="radio">` for native form
      // submission whose inline styles differ between SSR (kebab-case via
      // its server-rendered string) and React 19's client normalization
      // (camelCase). The mismatch is non-functional — only the visually-
      // hidden bubble input is affected — but produces a noisy console
      // error on every page that uses RadioGroup. Suppress at the item
      // boundary; safe because the visible button has no SSR-dependent
      // attributes of our own.
      suppressHydrationWarning
      className={cn(
        "aspect-square size-5 rounded-full border border-border-strong text-accent",
        "ring-offset-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:border-accent",
        "transition-colors duration-fast ease-standard",
        // When the parent RadioGroup is marked aria-invalid, paint each
        // item's border red so the invalid state is visible regardless
        // of which option is checked.
        "group-aria-[invalid=true]/radio:border-danger",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="size-2.5 fill-current text-current" aria-hidden="true" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
